/**
 * Campaign Lists Hooks
 * React Query hooks for campaign list operations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listResource, getResource, createResource, updateResource, deleteResource, apiFetch } from '@diggens/api-client';

/**
 * Hook to get all campaign lists
 */
export function useCampaignLists() {
  return useQuery({
    queryKey: ['campaign-lists'],
    queryFn: async () => {
      const { data } = await listResource('campaign_list');
      return data;
    },
  });
}

/**
 * Hook to get a single campaign list with members
 * Returns { list, members } where members is the embedded campaign_list_members array
 */
export function useCampaignListDetails(id: string | undefined) {
  return useQuery({
    queryKey: ['campaign-list', id],
    queryFn: async () => {
      const row = await getResource<Record<string, any>>('campaign_list', id!, { select: '*,members(*)' });
      const { members, ...list } = row;
      return { list, members: (members ?? []) as Array<Record<string, any>> };
    },
    enabled: !!id,
  });
}

/**
 * Hook to create a campaign list
 */
export function useCreateCampaignList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => createResource('campaign_list', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
    },
  });
}

/**
 * Hook to update a campaign list
 */
export function useUpdateCampaignList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => updateResource('campaign_list', id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-list', variables.id] });
    },
  });
}

/**
 * Hook to delete a campaign list
 */
export function useDeleteCampaignList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteResource('campaign_list', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
    },
  });
}

/**
 * Hook to add members to a list
 */
export function useAddListMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, nodeIds, status }: { listId: string; nodeIds: string[]; status?: 'suggested' | 'active' }) =>
      apiFetch(`/resource/campaign_list/${listId}/members`, {
        method: 'POST',
        body: JSON.stringify({ node_ids: nodeIds, status: status ?? 'active' }),
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-list', variables.listId] });
    },
  });
}

/**
 * Hook to update member status
 */
export function useUpdateMemberStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, nodeIds, status, removalReason }: { listId: string; nodeIds: string[]; status: 'suggested' | 'active' | 'removed'; removalReason?: string }) =>
      apiFetch(`/resource/campaign_list/${listId}/members/status`, {
        method: 'PATCH',
        body: JSON.stringify({ node_ids: nodeIds, status, removal_reason: removalReason }),
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-list', variables.listId] });
    },
  });
}

/**
 * Hook to remove members from a list
 */
export function useRemoveListMembers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, nodeIds }: { listId: string; nodeIds: string[] }) =>
      apiFetch(`/resource/campaign_list/${listId}/members`, {
        method: 'DELETE',
        body: JSON.stringify({ node_ids: nodeIds }),
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaign-lists'] });
      queryClient.invalidateQueries({ queryKey: ['campaign-list', variables.listId] });
    },
  });
}
